import { AbstractControl, ValidationErrors } from '@angular/forms';
import { EMAIL, PersonnelAppointment } from '@core/data-access';
import { PortalFieldConfig, PortalValidatorFn } from '@portal/form/data-access';
import { ApplicationModel, AuthorizedPersonnelModelKeys } from '@portal/application/data-access';
import { flatten } from 'lodash-es';
import { isObservable, map, Observable, of, take } from 'rxjs';

type ValidateAppointmentProps = {
  control: AbstractControl;
  field: PortalFieldConfig;
  options?: { application$?: Observable<any>; keyName?: string; businessClassification?: string };
};

function appointmentValidator({ control, options }: ValidateAppointmentProps): ValidationErrors | null {
  const personnels = control.getRawValue();
  const businessClassification = options?.businessClassification;
  const keyName = options?.keyName || 'appointment';

  if (!keyName || !Array.isArray(personnels)) return null;

  const keymanCount = totalPersonnelRoleKeyman(personnels, keyName);

  if (keymanCount < 1) return { 'must-have-keyman': true };
  if (keymanCount > 1) return { 'only-one-keyman': true };

  const hasKeyman = personnels.some(v => v?.appointment?.includes('AUTHORISED_SIGNATORY') && v?.appointment?.includes('KEYMAN'));
  if (!hasKeyman) return { 'must-have-keyman': true };

  const checks = {
    SOLE_PROPRIETORSHIP: {
      validate: () => personnels.some(v => v?.relationshipToApplicant?.includes('SOLE_PROPRIETOR')),
      message: 'must-have-sole-proprietor',
    },
    EXEMPT_PRIVATE_COMPANY: {
      validate: () => personnels.some(v => v?.appointment?.includes('AUTHORISED_SIGNATORY') && v?.appointment?.includes('DIRECTOR')),
      message: 'must-have-director',
    },
    LIMITED_LIABILITY_PARTNERSHIP: {
      validate: () => personnels.every(v => v?.appointment?.includes('AUTHORISED_SIGNATORY') && v?.appointment?.includes('SHAREHOLDER')),
      message: 'authorized-personnel-must-have-shareholder',
    },
    PARTNERSHIP: {
      validate: () => personnels.every(v => v?.appointment?.includes('AUTHORISED_SIGNATORY') && v?.appointment?.includes('SHAREHOLDER')),
      message: 'authorized-personnel-must-have-shareholder',
    },
    LIMITED_PARTNERSHIP: {
      validate: () => personnels.every(v => v?.appointment?.includes('AUTHORISED_SIGNATORY') && v?.appointment?.includes('SHAREHOLDER')),
      message: 'authorized-personnel-must-have-shareholder',
    },
  };

  if (businessClassification && businessClassification in checks && !checks[businessClassification].validate()) {
    return { [checks[businessClassification].message]: true };
  }

  return null;
}

export const appointmentAsyncValidator: PortalValidatorFn<Observable<ReturnType<typeof appointmentValidator>>> = (
  control,
  field,
  options,
) => {
  const application$ = options?.['application$'];

  if (!isObservable(application$)) return of(null);

  return application$.pipe(
    take(1),
    map(application => {
      if (!(application instanceof ApplicationModel)) return null;

      return appointmentValidator({
        options: { ...options, businessClassification: application.businessEntity?.businessClassification },
        control,
        field,
      });
    }),
  );
};

export function mustHaveAuthorisedSignatoryValidator(control: AbstractControl): ValidationErrors | null {
  const { value } = control;

  if (Array.isArray(value)) {
    const appointments: PersonnelAppointment[] = flatten(value?.map((v: object) => v[AuthorizedPersonnelModelKeys.appointment])) ?? [];
    const authorizedSignatories = appointments.filter(Boolean).filter(appointment => appointment === 'AUTHORISED_SIGNATORY');
    return authorizedSignatories.length < 1 ? { 'must-have-authorized-signatory': true } : null;
  }

  return null;
}

export function mustHaveShareholderValidator(control: AbstractControl): ValidationErrors | null {
  const { value } = control;
  if (Array.isArray(value)) {
    if (value.includes('SHAREHOLDER')) {
      return null;
    } else {
      return { 'must-have-shareholder': true };
    }
  }

  return null;
}

export function customEmailValidator(control: AbstractControl): ValidationErrors | null {
  if (!EMAIL.PATTERN.test(control.value)) {
    return { 'email-custom': true };
  }

  return null;
}

export const genderValidator: PortalValidatorFn<ValidationErrors | null> = (control, field) => {
  const isMyInfo = field?.options?.formState?.isMyInfo;
  return !isMyInfo && control.value === 'UNKNOWN' ? { required: true } : null;
};

export const keymanValidator: PortalValidatorFn<ValidationErrors | null> = (control, field, options) => {
  const personnels = control.getRawValue();
  const keyName = options?.['keyName'] || 'appointment';
  if (typeof keyName !== 'string' || !Array.isArray(personnels)) return null;

  const keymanCount = totalPersonnelRoleKeyman(personnels, keyName);
  if (keymanCount > 1) return { 'only-one-keyman': true };

  return null;
};

const totalPersonnelRoleKeyman = (personnels: any[], keyName: string): number => {
  return flatten(personnels.map(v => Array.from(new Set(v?.[keyName]))).filter(Boolean)).filter(appointment => appointment === 'KEYMAN')
    .length;
};
